<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDark, useToggle } from '@vueuse/core'

import Menu from 'primevue/menu'

import { useI18n } from 'vue-i18n'

import Logo from '@/assets/svg/logo.svg?component'
import Nebula from '@/assets/svg/nebula.svg?component'
import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import AppAICreditsFloatingButton from '@/components/molecules/ai/AppAICreditsFloatingButton.vue'
import SidebarItem from '@/components/atoms/sidebars/components/SidebarItem.vue'
import SidebarToggle from '@/components/atoms/sidebars/components/SidebarToggle.vue'
import { useSidebarRoutes } from '@/components/atoms/sidebars/composables/useSidebarRoutes'
import LogoMobile from '@/assets/svg/logo_mobile.svg?component'
import { useAuthStore } from '@/stores/useAuthStore'
import { useLogoutConfirm } from './composables/useLogoutConfirm'
import { useI18nStore } from '@/composables/useI18nStore'

const emit = defineEmits<{
  'update:expanded': [value: boolean]
  'update:visualExpanded': [value: boolean]
}>()

const { routes } = useSidebarRoutes()
const { t } = useI18n()
const authStore = useAuthStore()
const { confirmLogout } = useLogoutConfirm()
const i18nStore = useI18nStore()
const isDark = useDark({ selector: 'html' })
const toggleDark = useToggle(isDark)

const userMenu = ref()
const isPinned = ref(false)
const isHovering = ref(false)
const HOVER_OPEN_DELAY = 120
const HOVER_CLOSE_DELAY = 120
let hoverOpenTimer: ReturnType<typeof setTimeout> | null = null
let hoverCloseTimer: ReturnType<typeof setTimeout> | null = null
const isDesktop = ref(true)
let mq: MediaQueryList | null = null

const floatingMenu = ref({
  visible: false,
  items: [] as { title: string; path: string }[],
  x: 0,
  y: 0,
})

let closeTimeout: ReturnType<typeof setTimeout>

const updateDesktop = () => {
  isDesktop.value = mq ? mq.matches : true
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    mq = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1024px)')
    updateDesktop()
    mq?.addEventListener('change', updateDesktop)

    try {
      const saved = localStorage.getItem('app.sidebar.pinned')
      if (saved === '1') {
        isPinned.value = true
        emit('update:expanded', true)
      } else {
        emit('update:expanded', false)
      }
    } catch {}
  }
})

onBeforeUnmount(() => {
  mq?.removeEventListener('change', updateDesktop)
  if (hoverOpenTimer) clearTimeout(hoverOpenTimer)
  if (hoverCloseTimer) clearTimeout(hoverCloseTimer)
  if (closeTimeout) clearTimeout(closeTimeout)
})

const uiExpanded = computed(() => isPinned.value || (isHovering.value && isDesktop.value))

watch(
  uiExpanded,
  (value) => {
    emit('update:visualExpanded', value)
  },
  { immediate: true },
)

const toggleExpand = () => {
  const willPin = !isPinned.value
  isPinned.value = !isPinned.value

  if (!willPin) {
    if (hoverOpenTimer) {
      clearTimeout(hoverOpenTimer)
      hoverOpenTimer = null
    }
    if (hoverCloseTimer) {
      clearTimeout(hoverCloseTimer)
      hoverCloseTimer = null
    }
    isHovering.value = false
  }

  try {
    localStorage.setItem('app.sidebar.pinned', isPinned.value ? '1' : '0')
  } catch {}
  emit('update:expanded', isPinned.value)
}

const onMouseEnter = () => {
  if (!isDesktop.value) return
  if (hoverCloseTimer) {
    clearTimeout(hoverCloseTimer)
    hoverCloseTimer = null
  }
  if (hoverOpenTimer) clearTimeout(hoverOpenTimer)
  hoverOpenTimer = setTimeout(() => {
    isHovering.value = true
  }, HOVER_OPEN_DELAY)
}

const onMouseLeave = () => {
  if (!isDesktop.value) return
  if (hoverOpenTimer) {
    clearTimeout(hoverOpenTimer)
    hoverOpenTimer = null
  }
  if (hoverCloseTimer) clearTimeout(hoverCloseTimer)
  hoverCloseTimer = setTimeout(() => {
    isHovering.value = false
  }, HOVER_CLOSE_DELAY)
}

const openFloatingMenu = (event: MouseEvent, children: { title: string; path: string }[]) => {
  clearTimeout(closeTimeout)
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  floatingMenu.value = {
    visible: true,
    items: children,
    x: rect.right + 8,
    y: rect.top + rect.height / 2,
  }
}

const closeFloatingMenu = () => {
  closeTimeout = setTimeout(() => {
    floatingMenu.value.visible = false
  }, 200)
}

const cancelCloseFloatingMenu = () => {
  clearTimeout(closeTimeout)
}

const toggleUserMenu = (event: Event) => {
  userMenu.value.toggle(event)
}

const userMenuItems = computed(() => [
  {
    label: t(isDark.value ? 'sidebar.menu.lightMode' : 'sidebar.menu.darkMode'),
    icon: isDark.value ? 'pi pi-sun' : 'pi pi-moon',
    command: () => toggleDark(),
  },
  {
    label: `${t('sidebar.menu.language')}: ${t(
      i18nStore.language === 'es' ? 'sidebar.menu.spanish' : 'sidebar.menu.english',
    )}`,
    icon: 'pi pi-language',
    command: () => i18nStore.changeLanguage(i18nStore.language === 'es' ? 'en' : 'es'),
  },
  {
    separator: true,
  },
  {
    label: t('sidebar.menu.logout'),
    icon: 'pi pi-sign-out',
    command: () => confirmLogout(),
  },
])
</script>

<template>
  <div
    class="w-16 h-screen flex flex-col fixed top-0 left-0 transition-all duration-300 ease-in-out z-[50] shadow-[4px_0_10px_-3px_rgba(0,0,0,0.1)] backdrop-blur-2xl"
    :class="[
      { 'w-[200px]': uiExpanded },
      isDark
        ? 'border-r border-[var(--p-primary-800)]70 text-[var(--p-surface-50)]'
        : 'bg-[var(--p-primary-color)] text-[var(--p-primary-contrast-color)]',
    ]"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="py-2 px-2 relative">
      <router-link to="/" class="flex items-center justify-center h-16">
        <Logo
          v-if="uiExpanded"
          class="mr-8"
          :class="
            isDark
              ? 'h-7 w-auto transition-all duration-500 ease-in-out text-[var(--p-primary-color)] '
              : 'h-7 w-auto transition-all duration-500 ease-in-out '
          "
        />

        <LogoMobile
          v-else
          :class="
            isDark
              ? 'h-8 w-auto transition-all duration-500 ease-in-out text-[var(--p-primary-color)]'
              : 'h-8 w-auto transition-all duration-500 ease-in-out'
          "
        />
      </router-link>
      <SidebarToggle
        v-if="uiExpanded"
        :is-expanded="isPinned"
        @toggle="toggleExpand"
        class="absolute right-2 top-1/2 -translate-y-1/2"
      />
    </div>

    <div class="flex-1 flex flex-col overflow-y-auto px-2 py-3">
      <ul class="space-y-1 w-full">
        <SidebarItem
          v-for="route in routes"
          :key="route.path"
          :icon="route.icon"
          :label="route.title"
          :route="route.path"
          :is-expanded="uiExpanded"
          :is-active="$route.path.startsWith(route.path)"
          :badge-value="route.badgeValue"
          :badge-color="route.badgeColor"
          :disabled="route.disabled"
          :disabled-tooltip="route.disabledTooltip"
          :children="route.children"
          @open-floating-menu="openFloatingMenu"
          class="mb-1"
        />
      </ul>
    </div>

    <!-- Floating Submenu -->
    <transition name="slide-fade">
      <div
        v-if="floatingMenu.visible"
        class="fixed rounded-lg p-2 min-w-[180px] z-50"
        :class="
          isDark
            ? 'bg-[var(--p-surface-800)] text-[var(--p-surface-50)]'
            : 'bg-[var(--p-surface-0)] text-[var(--p-surface-900)]'
        "
        :style="{
          left: `${floatingMenu.x}px`,
          top: `${floatingMenu.y}px`,
          transform: 'translateY(-50%)',
        }"
        @mouseenter="cancelCloseFloatingMenu"
        @mouseleave="closeFloatingMenu"
      >
        <ul class="flex flex-col space-y-1">
          <li v-for="(child, idx) in floatingMenu.items" :key="idx">
            <router-link
              :to="child.path"
              class="flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-all duration-200 hover:bg-[var(--p-surface-100)] hover:text-[var(--p-primary-color)]"
            >
              <div class="w-1.5 h-1.5 rounded-full bg-[var(--p-surface-400)]"></div>
              {{ child.title }}
            </router-link>
          </li>
        </ul>
      </div>
    </transition>

    <!-- Perfil de usuario y logo Nebula -->
    <div class="relative mt-auto">
      <!-- Perfil de usuario -->
      <div
        class="flex flex-col p-3 transition-all duration-300 rounded-lg mx-2"
        :class="uiExpanded ? 'items-start gap-3' : 'items-center justify-center'"
      >
        <div
          class="flex w-full items-center transition-all duration-300"
          :class="uiExpanded ? 'justify-between' : 'justify-center'"
        >
          <div class="relative">
            <AppAvatar
              @click="toggleUserMenu"
              class="flex-shrink-0 !w-13 !h-13 !text-sm text-white! font-bold ring-2 ring-transparent shadow-md cursor-pointer transition-all duration-300 hover:scale-105 hover:ring-[var(--p-primary-400)] dark:hover:ring-[var(--p-primary-500)] [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:object-center"
              :class="[
                isDark
                  ? '!bg-[var(--p-primary-color)]/30 !text-[var(--p-primary-contrast-color)] ring-[var(--p-primary-color)]/20'
                  : '!bg-[var(--p-surface-0)]/30 !text-[var(--p-primary-color)] ring-[var(--p-surface-0)]/30',
              ]"
              :label="
                authStore.user?.name
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase() || 'U'
              "
              :image="authStore.user?.avatar || null"
              size="large"
            />
            <!-- <AppAICreditsFloatingButton v-if="!uiExpanded" collapsed /> -->
          </div>

          <!-- <AppAICreditsFloatingButton v-if="uiExpanded" allow-purchase /> -->
        </div>

        <div
          v-if="uiExpanded"
          class="flex flex-col min-w-0 flex-1 transition-all duration-300 ease-in-out overflow-hidden"
        >
          <span class="quicksand text-sm truncate">{{ authStore.user?.name || 'Usuario' }}</span>
          <span class="quicksand text-xs opacity-70 truncate">
            {{ authStore.user?.email || '' }}
          </span>
        </div>
      </div>

      <!-- Logo Nebula -->
      <div
        class="flex px-2 gap-x-1 pb-2"
        :class="
          uiExpanded ? 'items-center justify-start ml-3 mb-2 mr-2' : 'items-center justify-center'
        "
      >
        <Nebula
          class="h-4 transition-all duration-300"
          :class="[uiExpanded ? 'w-auto' : 'w-10', isDark ? 'text-(--p-primary-color)' : '']"
        />
        <span class="text-[8px]">2.0.13</span>
      </div>

      <!-- Menu de usuario -->
      <Menu
        ref="userMenu"
        :model="userMenuItems"
        :popup="true"
        class="!rounded-lg text-sm !border !border-white/40 !bg-primary/20 !shadow-xl !backdrop-blur-2xl !backdrop-saturate-150 dark:!border-[var(--p-primary-color)]/30 dark:bg-[var(--p-primary-950)]/70 [&_.p-menu-list]:!bg-transparent [&_.p-menu-item-content]:!bg-transparent [&_.p-menu-item-content:hover]:!bg-[var(--p-primary-color)]/12 [&_.p-menu-item-icon]:!text-[var(--p-primary-50)] dark:[&_.p-menu-item-icon]:!text-[var(--p-primary-50)] [&_.p-menu-item-label]:!text-[var(--p-surface-0)] dark:[&_.p-menu-item-label]:!text-[var(--p-primary-50)] [&_.p-menu-item-icon]:!text-[12px]"
      />
    </div>
  </div>
</template>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
  transform-origin: left center;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
